/**
 * Data Access Layer for Project Call Cost Analysis
 * 
 * Fetches LLM calls by project ID with period-based filtering
 * Handles pagination for complete data retrieval across multiple projects
 */

import { API, graphqlOperation } from 'aws-amplify';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import { callsByProjectIdAndCreatedAt } from '../graphql/queries';
import { CallsByProjectIdAndCreatedAtQuery, CallsByProjectIdAndCreatedAtQueryVariables, LLMCall, ModelSortDirection } from '../API';
import { PeriodData } from '../utils/periodCalculations';

export interface ProjectCallsResult {
  calls: LLMCall[];
  totalCount: number;
  pagesRetrieved: number;
  fetchTimeMs: number;
  truncated: boolean; // True if hit the safety limit
}

/**
 * Fetch all LLM calls for a project within a specific period
 * Handles pagination automatically to ensure complete data retrieval
 */
export async function fetchLLMCallsByProjectIdAndPeriod(
  projectId: string,
  period: PeriodData
): Promise<ProjectCallsResult> {
  const startTime = Date.now();
  const allCalls: LLMCall[] = [];
  let nextToken: string | null = null;
  let hasMorePages = true;
  let pagesRetrieved = 0;
  let truncated = false;
  
  while (hasMorePages) {
    const result = await fetchLLMCallsByProjectIdRecursive(
      projectId,
      period,
      nextToken
    ) as GraphQLResult<CallsByProjectIdAndCreatedAtQuery>;
    
    pagesRetrieved++;
    
    if (result.data?.callsByProjectIdAndCreatedAt?.items) {
      const items = result.data.callsByProjectIdAndCreatedAt.items.filter(
        (item: any): item is LLMCall => item !== null
      );
      allCalls.push(...items);
    }
    
    nextToken = result.data?.callsByProjectIdAndCreatedAt?.nextToken || null;
    hasMorePages = !!nextToken;
    
    // Safety break for projects with excessive call volumes (higher limit than customer billing)
    if (allCalls.length > 100000) {
      console.warn(`Project ${projectId} has >100k calls, truncating for performance`);
      truncated = true;
      break;
    }
    
    // Additional safety: prevent infinite loops with too many pages
    if (pagesRetrieved > 200) {
      console.warn(`Project ${projectId} required >200 pages, stopping for performance`);
      truncated = true;
      break;
    }
  }
  
  const fetchTimeMs = Date.now() - startTime;
  
  console.log(`🔍 [Project Fetch Debug] Final Summary:`, {
    projectId,
    periodType: period.type,
    periodLabel: period.label,
    totalCalls: allCalls.length,
    pagesRetrieved,
    fetchTimeMs,
    truncated,
    dateRange: `${period.startDate.toISOString()} to ${(period.actualEndDate || period.endDate).toISOString()}`
  });
  
  console.log(`Fetched ${allCalls.length} calls for project ${projectId} in ${fetchTimeMs}ms (${pagesRetrieved} pages)`);
  
  return {
    calls: allCalls,
    totalCount: allCalls.length,
    pagesRetrieved,
    fetchTimeMs,
    truncated
  };
}

/**
 * Internal recursive helper function with UTC period filtering
 * Fetches a single page of results with proper GraphQL query structure
 */
async function fetchLLMCallsByProjectIdRecursive(
  projectId: string,
  period: PeriodData,
  nextToken: string | null = null
): Promise<GraphQLResult<CallsByProjectIdAndCreatedAtQuery>> {
  
  // Build the query variables according to the GraphQL schema
  const variables: CallsByProjectIdAndCreatedAtQueryVariables = {
    projectId: projectId,
    sortDirection: ModelSortDirection.DESC, // Most recent first
    limit: 1000, // Use larger page size for fewer round trips
    nextToken: nextToken
  };

  // Add UTC-based period filtering using the createdAt range
  const actualEndDate = period.actualEndDate || period.endDate;
  variables.createdAt = {
    between: [
      period.startDate.toISOString(),
      actualEndDate.toISOString()
    ]
  };

  console.log(`🔍 [Project Query Debug] Fetching calls for project: ${projectId}`);
  console.log(`🔍 [Project Query Debug] Period: ${period.type} (${period.label})`);
  console.log(`🔍 [Project Query Debug] Date range: ${period.startDate.toISOString()} to ${actualEndDate.toISOString()}`);

  try {
    const result = await API.graphql(
      graphqlOperation(callsByProjectIdAndCreatedAt, variables)
    ) as GraphQLResult<CallsByProjectIdAndCreatedAtQuery>;

    console.log(`🔍 [Project Query Debug] Query result:`, {
      itemsCount: result.data?.callsByProjectIdAndCreatedAt?.items?.length || 0,
      hasNextToken: !!result.data?.callsByProjectIdAndCreatedAt?.nextToken,
      firstItem: result.data?.callsByProjectIdAndCreatedAt?.items?.[0] ? {
        id: result.data.callsByProjectIdAndCreatedAt.items[0].id,
        createdAt: result.data.callsByProjectIdAndCreatedAt.items[0].createdAt,
        projectId: result.data.callsByProjectIdAndCreatedAt.items[0].projectId
      } : null,
      errors: result.errors
    });

    return result;
  } catch (error) {
    console.error('Error fetching project calls:', error);
    throw new Error(`Failed to fetch calls for project ${projectId}: ${error}`);
  }
}

/**
 * Fetch a limited preview of project calls (for quick loading)
 * Returns the most recent 1000 calls without full pagination
 */
export async function fetchLLMProjectCallsPreview(
  projectId: string,
  period: PeriodData
): Promise<ProjectCallsResult> {
  const startTime = Date.now();
  
  try {
    const result = await fetchLLMCallsByProjectIdRecursive(projectId, period, null);
    
    const calls = result.data?.callsByProjectIdAndCreatedAt?.items?.filter(
      (item: any): item is LLMCall => item !== null
    ) || [];
    
    const fetchTimeMs = Date.now() - startTime;
    
    return {
      calls,
      totalCount: calls.length,
      pagesRetrieved: 1,
      fetchTimeMs,
      truncated: !!result.data?.callsByProjectIdAndCreatedAt?.nextToken // True if more data available
    };
  } catch (error) {
    console.error('Error fetching project calls preview:', error);
    throw error;
  }
}

/**
 * Validate project ID and period before making requests
 */
export function validateProjectBillingRequest(projectId: string, period: PeriodData): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (!projectId || projectId.trim() === '') {
    errors.push('Project ID is required');
  }
  
  if (!period.startDate || !period.endDate) {
    errors.push('Period start and end dates are required');
  }
  
  if (period.startDate >= period.endDate) {
    errors.push('Period start date must be before end date');
  }
  
  // Check for reasonable period length (not more than 1 year)
  const maxPeriodMs = 365 * 24 * 60 * 60 * 1000; // 1 year in milliseconds
  if (period.endDate.getTime() - period.startDate.getTime() > maxPeriodMs) {
    errors.push('Period length cannot exceed 1 year');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Performance monitoring interface for project billing operations
 */
export interface ProjectBillingPerformanceMetrics {
  totalCallsProcessed: number;
  fetchTimeMs: number;
  calculationTimeMs: number;
  pagesRetrieved: number;
  cacheHit: boolean;
  memoryUsageMB?: number;
}

/**
 * Log performance metrics for monitoring and optimization
 */
export function logProjectBillingPerformance(
  projectId: string,
  period: PeriodData,
  metrics: ProjectBillingPerformanceMetrics
): void {
  const logData = {
    projectId,
    periodType: period.type,
    periodDays: period.totalDays,
    ...metrics,
    timestamp: new Date().toISOString()
  };
  
  // Log performance data (in production, this could go to monitoring service)
  if (metrics.totalCallsProcessed > 50000 || metrics.fetchTimeMs > 60000) {
    console.warn('High-volume project billing operation:', logData);
  } else {
    console.log('Project billing operation completed:', logData);
  }
}
